import React, { useEffect, useState } from 'react';

import { Home } from '@mui/icons-material';
import dayjs from 'dayjs';
import utc from 'dayjs/plugin/utc';
import { useSelector } from 'react-redux';
import { useLocation, useNavigate } from 'react-router-dom';

import FormContent from './FormContent';
import { createCenterlineSurvey, getCountForPushNotification, sendPushNotification, getList } from './operations';
import { Breadcrumbs, Footer, ModalConfirmation, SubHeader } from '../../../components';
import './styles.scoped.css';

dayjs.extend(utc);

const customStyles = width => ({
  control: provided => ({
    ...provided,
    width: width,
    height: '30px',
    minWidth: '300px',
  }),
  menuList: provided => ({
    ...provided,
    maxHeight: '200px',
  }),
  menu: provided => ({
    ...provided,
    zIndex: 100000,
  }),
  multiValue: provided => ({
    ...provided,
    backgroundColor: '#da0f2e',
  }),
  multiValueLabel: provided => ({
    ...provided,
    color: '#ffffff',
  }),
  multiValueRemove: provided => ({
    ...provided,
    color: '#ffffff',
  }),
});

const AddCenterlineSurvey = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const user = useSelector(state => state.admin?.user);

  const [form, setForm] = useState({
    type: 'National',
    subject: 'Driver Survey',
    message: '',
    url: '',
    status: 'Pending',
    lob: '',
    branch: '',
    recipientStatus: '',
    approvedBy: '',
    approvedAt: null,
    createdBy: user?.name,
    createdAt: dayjs().utc().format(),
    sentOn: null,
  });

  const [branches, setBranches] = useState([]);
  const [lobs, setLOBs] = useState([]);
  const [recipientStatuses, setRecipientStatuses] = useState([]);

  const [isLoadingBranches, setIsLoadingBranches] = useState(false);
  const [isLoadingLOBs, setIsLoadingLOBs] = useState(false);
  const [isLoadingRecipientStatus, setIsLoadingRecipientStatus] = useState(false);

  const [showConfirmationModal, setShowConfirmationModal] = useState(false);
  const [showDriversCountModal, setShowDriversCountModal] = useState(false);
  const [showSendErrorModal, setShowSendErrorModal] = useState(false);
  const [showErrorModal, setShowErrorModal] = useState(false);
  const [showExecutionTimeoutException, setShowExecutionTimeoutException] = useState(false);

  const [driversToBeNotified, setDriversToBeNotifed] = useState(0);
  const [driversNotified, setDriversNotifed] = useState(0);
  const [isLoading, setIsLoading] = useState(false);
  const [isLoadingNotifiedNotifications, setIsLoadingNotifiedNotifications] = useState(false);
  const [errors, setErrors] = useState(false);

  const handleGetRecipientStatuses = async () => {
    setIsLoadingRecipientStatus(true);
    try {
      const responses = await getList('driver-status');
      setRecipientStatuses(responses);
    } catch (e) {
      console.log('e');
    }
    setIsLoadingRecipientStatus(false);
  };

  const fieldsAreValid = () => {
    let valid = true;
    let formErrorsCopy = { ...errors };

    const requiredFields = ['recipientStatus'];

    if (!form?.recipientStatus) {
      formErrorsCopy = {
        ...formErrorsCopy,
        recipientStatus: 'Please provide a valid value.',
      };
      valid = false;
    } else {
      formErrorsCopy = {
        ...formErrorsCopy,
        recipientStatus: '',
      };
      valid = true;
    }

    requiredFields.forEach(field => {
      if (!form[field] || form[field] === '') {
        formErrorsCopy = {
          ...formErrorsCopy,
          [field]: 'Please provide a valid value.',
        };
        valid = false;
      }
    });

    setErrors(formErrorsCopy);

    return valid;
  };

  const handleCheckbox = async e => {
    setForm({
      ...form,
      approvedBy: e.target.checked ? user.name : '',
      status: e.target.checked ? 'Sent' : 'Pending',
      approvedAt: e.target.checked ? dayjs().utc().format() : null,
    });
  };

  const handleRadio = async value => {
    setForm({
      ...form,
      type: value,
    });
  };

  const confirmPushNotification = async () => {
    setIsLoading(true);

    if (fieldsAreValid()) {
      if (form?.approvedBy !== user?.name) {
        setShowSendErrorModal(true);
      }

      if (form?.status === 'Sent') {
        try {
          const response = await getDriversToBeNotifiedForPushNotifications();
          return response;
        } catch (e) {
          console.log('error', e);
        }
      }
    }

    setIsLoading(false);
    return null;
  };

  const getDriversToBeNotifiedForPushNotifications = async () => {
    try {
      const response = await getCountForPushNotification({
        ...form,
        sentOn: dayjs().utc().format(),
      });

      if (response?.responseCode === 200) {
        setForm({
          ...form,
          sentOn: dayjs().utc().format(),
        });
        setIsLoading(false);
        setDriversToBeNotifed(response?.driverCount);
        setShowDriversCountModal(true);
      } else if (response?.responseCode === 404) {
        setIsLoading(false);
        setDriversToBeNotifed(response?.driverCount);
        setShowDriversCountModal(true);
        setForm({
          ...form,
          status: 'Pending',
          approvedBy: '',
          approvedAt: null,
        });
      }

      return response;
    } catch (e) {
      setShowErrorModal(true);
      console.log('error', e);
    }
    return null;
  };

  const sendBulkPushNotification = async () => {
    setIsLoadingNotifiedNotifications(true);

    try {
      const response = await sendPushNotification(form);

      if (
        response?.data?.publishPushNotification?.responseCode === 200 &&
        response?.data?.publishPushNotification?.driverCount > 0
      ) {
        setShowDriversCountModal(false);
        setDriversNotifed(response?.data?.publishPushNotification?.driverCount);
        setIsLoadingNotifiedNotifications(false);
        setShowConfirmationModal(true);
        await createCenterlineSurvey(form);
      }

      if (
        response?.data?.publishPushNotification?.responseCode === 404 &&
        response?.data?.publishPushNotification?.responseMessage !== 'Success'
      ) {
        setDriversToBeNotifed(response?.data?.publishPushNotification?.driverCount);
        setShowDriversCountModal(true);
        setIsLoadingNotifiedNotifications(false);
        setForm({
          ...form,
          status: 'Pending',
        });
      }

      if (response?.errors !== undefined && response?.errors[0]?.errorType === 'Lambda:ExecutionTimeoutException') {
        setIsLoading(false);
        setIsLoadingNotifiedNotifications(false);
        setShowConfirmationModal(false);
        setShowExecutionTimeoutException(true);
      }
      return response;
    } catch (e) {
      console.log('error', e);
      setIsLoadingNotifiedNotifications(false);
      setShowDriversCountModal(false);
      setShowErrorModal(true);
    }
    return null;
  };

  const setField = (fields, value) => {
    if (Array.isArray(fields)) {
      const itemsToAdd = {};
      const newErrors = { ...errors };
      fields.forEach(field => {
        itemsToAdd[field?.fieldName] = field?.fieldValue;
        if (errors && !!errors[field.fieldName]) delete newErrors[field?.fieldName];
      });
      setForm({
        ...form,
        ...itemsToAdd,
      });

      setErrors({
        ...newErrors,
      });
    } else {
      setForm({
        ...form,
        [fields]: value,
      });
      if (errors && !!errors[fields]) {
        setErrors({
          ...errors,
          [fields]: null,
        });
      }
    }
  };

  const handleCancel = () => {
    setDriversToBeNotifed(0);
    setDriversNotifed(0);
    navigate(-1);
  };

  const handleGetBranches = async () => {
    setIsLoadingBranches(true);
    try {
      const responses = await getList('branches');
      console.log('responses', responses);
      setBranches(responses);
    } catch (e) {
      console.log('e', e);
    }
    setIsLoadingBranches(false);
  };

  const handleGetLOB = async () => {
    setIsLoadingLOBs(true);
    try {
      const responses = await getList('line-of-business');
      setLOBs(responses);
    } catch (e) {
      console.log('e', e);
    }
    setIsLoadingLOBs(false);
  };

  useEffect(() => {
    if (form?.branch === '' && form?.lob === '') {
      handleGetBranches();
      handleGetLOB();
    }
  }, [form?.branch, form?.lob]);

  useEffect(() => {
    handleGetRecipientStatuses();
  }, []);

  return (
    <>
      <SubHeader
        text="Push Notifications"
        buttons={[
          { icon: <Home sx={{ color: '#fff', fontSize: 30 }} />, onPress: () => navigate('/push-notifications') },
        ]}
      />

      <Breadcrumbs
        wrapStyle={{ margin: 0 }}
        leftTitle="PN Home"
        middleTitle="Centerline Survey"
        midItemAction={() => navigate('../push-notifications/centerline-survey', { replace: true })}
        rightTitle="Add Centerline Survey"
        route="/push-notifications"
        style={{ marginLeft: 30 }}
      />

      <FormContent
        confirmPushNotification={confirmPushNotification}
        customStyles={customStyles}
        errors={errors}
        headerTitle="New Centerline Survey"
        handleCancel={handleCancel}
        handleCheckbox={handleCheckbox}
        handleRadio={handleRadio}
        isLoadingBranches={isLoadingBranches}
        isLoadingLOBs={isLoadingLOBs}
        recipientStatuses={recipientStatuses}
        isLoadingRecipientStatus={isLoadingRecipientStatus}
        form={form}
        setForm={setForm}
        isLoading={isLoading}
        location={location}
        setField={setField}
        lobs={lobs}
        branches={branches}
      />

      <ModalConfirmation
        title="Send Confirmation"
        body={`This will be sent to ${driversToBeNotified} drivers`}
        isOpen={showDriversCountModal}
        primaryButton={driversToBeNotified > 0}
        optionalClose
        primaryButtonTitle="Send"
        secondaryButtonTitle="Cancel"
        isLoading={isLoadingNotifiedNotifications}
        onClick={async () => {
          sendBulkPushNotification();
        }}
        secondaryOnClick={() => {
          setShowDriversCountModal(false);
        }}
      />

      <ModalConfirmation
        title="Error"
        body="There was an error with the push notification. Please contact support."
        primaryButtonTitle="Close"
        onClick={() => {
          sendBulkPushNotification();
          navigate(-1);
        }}
        isOpen={showErrorModal}
        primaryButton
      />

      <ModalConfirmation
        title="Error"
        body="Execution Timeout Exception."
        primaryButtonTitle="Try Again"
        secondaryButtonTitle="Cancel"
        optionalClose
        onClick={() => {
          setShowExecutionTimeoutException(false);
          sendBulkPushNotification();
        }}
        secondaryOnClick={() => {
          setForm({
            ...form,
            status: 'Pending',
          });
          setShowExecutionTimeoutException(false);
        }}
        isOpen={showExecutionTimeoutException}
        primaryButton
      />

      <ModalConfirmation
        title="Send error"
        body='Check the "Approve to Send" box and then save to send. Otherwise click Cancel to exit'
        isOpen={showSendErrorModal}
        primaryButton
        primaryButtonTitle="Ok"
        onClick={async () => {
          setShowSendErrorModal(false);
        }}
      />

      <ModalConfirmation
        title="Send Confirmation"
        body={`This was sent to ${driversNotified} drivers`}
        isOpen={showConfirmationModal}
        primaryButton={driversNotified > 0}
        primaryButtonTitle="Ok"
        onClick={async () => {
          setShowConfirmationModal(false);
          navigate(-1);
        }}
      />

      <Footer />
    </>
  );
};

export default AddCenterlineSurvey;
